import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, Camera, CameraOff, X, Ticket, Sparkles, AlertCircle } from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';

/**
 * Full-screen QR Scanner component for benefit redemption
 * Based on CLAB OS CheckInPage scanner implementation
 */
const BenefitScanner = ({ benefit, onClose, onSuccess }) => {
    const { api, user } = useAuth();
    const [scanResult, setScanResult] = useState(null);
    const [showPopup, setShowPopup] = useState(false);
    const [loading, setLoading] = useState(false);
    const [cameraActive, setCameraActive] = useState(false);
    const [cameraReady, setCameraReady] = useState(false);
    const [cameraError, setCameraError] = useState(null);

    const html5QrCodeRef = useRef(null);
    const isProcessingRef = useRef(false);
    const lastScannedRef = useRef('');
    const scanCooldownRef = useRef(null);
    const isMountedRef = useRef(true);

    // Disable scroll/zoom on mount - app-like behavior
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
        document.body.style.height = '100%';
        document.body.style.touchAction = 'none';

        const preventZoom = (e) => {
            if (e.touches && e.touches.length > 1) e.preventDefault();
        };
        const preventGesture = (e) => e.preventDefault();

        document.addEventListener('touchmove', preventZoom, { passive: false });
        document.addEventListener('gesturestart', preventGesture);
        document.addEventListener('gesturechange', preventGesture);

        return () => {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
            document.body.style.height = '';
            document.body.style.touchAction = '';
            document.removeEventListener('touchmove', preventZoom);
            document.removeEventListener('gesturestart', preventGesture);
            document.removeEventListener('gesturechange', preventGesture);
        };
    }, []);

    useEffect(() => {
        isMountedRef.current = true;
        // Auto-start camera
        setCameraActive(true);
        setTimeout(() => initCamera(), 100);
        
        return () => {
            isMountedRef.current = false;
            cleanupCamera();
        };
    }, []);

    const cleanupCamera = async () => {
        if (scanCooldownRef.current) {
            clearTimeout(scanCooldownRef.current);
            scanCooldownRef.current = null;
        }
        if (html5QrCodeRef.current) {
            try {
                const state = html5QrCodeRef.current.getState();
                if (state === 2) await html5QrCodeRef.current.stop();
            } catch (e) {
                console.log('Camera cleanup:', e);
            }
            html5QrCodeRef.current = null;
        }
    };

    const initCamera = async () => {
        if (html5QrCodeRef.current) return;
        setCameraError(null);
        
        try {
            const qrScanner = new Html5Qrcode("qr-reader", { verbose: false });
            html5QrCodeRef.current = qrScanner;

            await qrScanner.start(
                { facingMode: "environment" },
                { 
                    fps: 10, 
                    qrbox: { width: 280, height: 280 }, 
                    aspectRatio: 1.0, 
                    disableFlip: false 
                },
                onScanSuccess,
                () => {}
            );
            if (isMountedRef.current) setCameraReady(true);
        } catch (err) {
            console.error("Camera init error:", err);
            if (isMountedRef.current) {
                setCameraActive(false);
                setCameraReady(false);
                setCameraError(err.message || 'Kamera konnte nicht gestartet werden');
            }
        }
    };

    const onScanSuccess = (decodedText) => {
        if (isProcessingRef.current) return;
        if (decodedText === lastScannedRef.current) return;

        lastScannedRef.current = decodedText;
        processScan(decodedText);

        if (scanCooldownRef.current) clearTimeout(scanCooldownRef.current);
        scanCooldownRef.current = setTimeout(() => {
            lastScannedRef.current = '';
        }, 5000);
    };

    const processScan = async (qrCode) => {
        if (!qrCode || isProcessingRef.current) return;

        isProcessingRef.current = true;
        setLoading(true);

        try {
            const response = await api.post('/benefits/scan', { qr_code: qrCode });
            
            if (isMountedRef.current) {
                setScanResult(response.data);
                setShowPopup(true);
                playBeep(response.data.valid);
                
                if (response.data.valid && onSuccess) {
                    onSuccess(response.data);
                }
            }
        } catch (error) {
            if (isMountedRef.current) {
                setScanResult({ 
                    valid: false, 
                    message: error.response?.data?.detail || 'Verbindungsfehler',
                    error_code: 'NETWORK_ERROR' 
                });
                setShowPopup(true);
                playBeep(false);
            }
        } finally {
            setLoading(false);
            setTimeout(() => { isProcessingRef.current = false; }, 800);
        }
    };

    const playBeep = (success) => {
        try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.frequency.value = success ? 880 : 220;
            osc.type = 'sine';
            gain.gain.value = 0.2;
            osc.start();
            setTimeout(() => { osc.stop(); ctx.close(); }, success ? 100 : 250);
        } catch (e) {
            console.log('Audio not available');
        }
    };

    const closePopup = () => {
        setShowPopup(false);
        // If scan was successful, close entire scanner and go back to benefits page
        if (scanResult?.valid) {
            setTimeout(() => {
                handleClose();
            }, 300);
        } else {
            setTimeout(() => setScanResult(null), 300);
        }
    };

    const handleClose = () => {
        cleanupCamera();
        onClose();
    };

    return ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col">
            {/* Header - with safe-area padding for iOS notch */}
            <div 
                className="relative px-4 py-4 flex items-center justify-between border-b border-white/10"
                style={{ paddingTop: 'calc(1rem + env(safe-area-inset-top))' }}
            >
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                        <Ticket className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                        <h1 className="text-white font-bold">Vorteil einlösen</h1>
                        {benefit && (
                            <p className="text-white/60 text-sm">{benefit.name}</p>
                        )}
                    </div>
                </div>
                <button 
                    onClick={handleClose}
                    className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                >
                    <X className="w-5 h-5 text-white" />
                </button>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col items-center justify-center p-4">
                {/* Camera View */}
                {cameraActive ? (
                    <div className="relative w-full max-w-sm aspect-square">
                        {/* Scanner container */}
                        <div 
                            id="qr-reader" 
                            className="w-full h-full rounded-2xl overflow-hidden bg-black"
                            data-testid="qr-scanner"
                        />
                        
                        {/* Loading overlay */}
                        {!cameraReady && !cameraError && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/80 rounded-2xl">
                                <div className="text-center">
                                    <motion.div 
                                        animate={{ rotate: 360 }}
                                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                                        className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full mx-auto mb-3"
                                    />
                                    <p className="text-white/60 text-sm">Kamera lädt...</p>
                                </div>
                            </div>
                        )}

                        {/* Camera error */}
                        {cameraError && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/80 rounded-2xl">
                                <div className="text-center px-4">
                                    <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
                                    <p className="text-white font-medium mb-2">Kamera-Fehler</p>
                                    <p className="text-white/60 text-sm mb-4">{cameraError}</p>
                                    <Button onClick={() => {
                                        setCameraError(null);
                                        initCamera();
                                    }} variant="outline" size="sm">
                                        Erneut versuchen
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Scan indicator when ready */}
                        {cameraReady && (
                            <div className="absolute inset-0 pointer-events-none">
                                {/* Corner markers */}
                                <div className="absolute top-4 left-4 w-12 h-12 border-t-4 border-l-4 border-primary rounded-tl-xl" />
                                <div className="absolute top-4 right-4 w-12 h-12 border-t-4 border-r-4 border-primary rounded-tr-xl" />
                                <div className="absolute bottom-4 left-4 w-12 h-12 border-b-4 border-l-4 border-primary rounded-bl-xl" />
                                <div className="absolute bottom-4 right-4 w-12 h-12 border-b-4 border-r-4 border-primary rounded-br-xl" />
                                
                                {/* Scan line animation */}
                                <motion.div 
                                    className="absolute left-4 right-4 h-0.5 bg-gradient-to-r from-transparent via-primary to-transparent"
                                    animate={{ top: ['15%', '85%', '15%'] }}
                                    transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                                />
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="text-center">
                        <CameraOff className="w-16 h-16 text-white/30 mx-auto mb-4" />
                        <p className="text-white/60 mb-4">Kamera nicht aktiv</p>
                        <Button onClick={() => {
                            setCameraActive(true);
                            setTimeout(() => initCamera(), 100);
                        }}>
                            <Camera className="w-4 h-4 mr-2" />
                            Kamera aktivieren
                        </Button>
                    </div>
                )}

                {/* Instructions */}
                <div className="mt-6 text-center">
                    <p className="text-white/60 text-sm">
                        Halte den QR-Code in die Kamera
                    </p>
                </div>
            </div>

            {/* Footer with user info - with safe-area padding for iOS home indicator */}
            <div 
                className="px-4 py-4 border-t border-white/10 text-center"
                style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
            >
                <p className="text-white/40 text-xs">
                    Eingeloggt als: <span className="text-white/60">{user?.first_name} {user?.last_name}</span>
                </p>
            </div>

            {/* Result Popup */}
            <AnimatePresence>
                {showPopup && scanResult && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/90 flex items-center justify-center p-4 z-[10000]"
                        onClick={closePopup}
                    >
                        <motion.div
                            initial={{ scale: 0.9, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 20 }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full max-w-sm"
                        >
                            {scanResult.valid ? (
                                /* SUCCESS */
                                <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-3xl p-6 text-center shadow-2xl">
                                    <motion.div
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        transition={{ type: 'spring', bounce: 0.5 }}
                                    >
                                        <CheckCircle className="w-20 h-20 text-white mx-auto mb-4" />
                                    </motion.div>
                                    
                                    <h2 className="text-2xl font-black text-white mb-2">
                                        ERFOLGREICH!
                                    </h2>
                                    
                                    <div className="bg-white/20 rounded-xl p-4 mb-4">
                                        <p className="text-white/80 text-sm mb-1">Vorteil</p>
                                        <p className="text-white font-bold text-lg">{scanResult.benefit_name}</p>
                                        <div className="mt-2 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white/20">
                                            <Sparkles className="w-4 h-4 text-yellow-300" />
                                            <span className="text-white font-bold">{scanResult.discount}</span>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-3 mb-4">
                                        <div className="bg-white/10 rounded-xl p-3">
                                            <p className="text-white/60 text-xs">Mitarbeiter</p>
                                            <p className="text-white font-medium">{scanResult.user_name}</p>
                                        </div>
                                        <div className="bg-white/10 rounded-xl p-3">
                                            <p className="text-white/60 text-xs">Einlösungen</p>
                                            <p className="text-white font-medium">
                                                {scanResult.redemption_count}
                                                {scanResult.max_redemptions && ` / ${scanResult.max_redemptions}`}
                                            </p>
                                        </div>
                                    </div>

                                    <Button 
                                        onClick={closePopup}
                                        className="w-full h-12 text-base font-semibold rounded-2xl bg-white/20 hover:bg-white/30 text-white border border-white/30"
                                    >
                                        Fertig - Zurück zur Übersicht
                                    </Button>
                                </div>
                            ) : (
                                /* ERROR */
                                <div className="bg-gradient-to-br from-red-600 to-red-700 rounded-3xl p-6 text-center shadow-2xl">
                                    <motion.div
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        transition={{ type: 'spring', bounce: 0.5 }}
                                    >
                                        <XCircle className="w-20 h-20 text-white mx-auto mb-4" />
                                    </motion.div>
                                    
                                    <h2 className="text-2xl font-black text-white mb-2">
                                        FEHLER
                                    </h2>
                                    
                                    <p className="text-white/90 text-lg mb-4">
                                        {scanResult.message}
                                    </p>

                                    {scanResult.benefit_name && (
                                        <div className="bg-white/10 rounded-xl p-3 mb-4">
                                            <p className="text-white/60 text-xs">Vorteil</p>
                                            <p className="text-white font-medium">{scanResult.benefit_name}</p>
                                        </div>
                                    )}

                                    {scanResult.error_code === 'ALREADY_MAXED' && (
                                        <div className="bg-white/10 rounded-xl p-3 mb-4">
                                            <p className="text-white/80 text-sm">
                                                {scanResult.redemption_count} von {scanResult.max_redemptions} Einlösungen verbraucht
                                            </p>
                                        </div>
                                    )}

                                    <div className="space-y-2">
                                        <Button 
                                            onClick={() => {
                                                setShowPopup(false);
                                                setScanResult(null);
                                            }}
                                            className="w-full h-12 text-base font-semibold rounded-2xl bg-white/20 hover:bg-white/30 text-white border border-white/30"
                                        >
                                            Erneut versuchen
                                        </Button>
                                        <Button 
                                            onClick={closePopup}
                                            variant="ghost"
                                            className="w-full h-10 text-sm text-white/60 hover:text-white hover:bg-white/10"
                                        >
                                            Abbrechen
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>,
        document.body
    );
};

export default BenefitScanner;
